import React, { createContext, useContext, useEffect, useState } from "react";
import { VenueUIConfigOptions } from "../types/Config";
import {
  createDisplayConfig,
  defaultPalette,
  defaultScaling,
  getPalette,
  PaletteConfiguration,
  ScalingConfiguration,
} from "../util/ThemeUtil";
import { useWindowSize, WindowSize } from "../util/useWindowSize";
import { useConnection } from "./ConnectionContext";
import { useSearchParams } from "./SearchParamsContext";

type ConfigurationContextType = {
  config: Configuration;
  thinConfig: Configuration;
};

export type DisplayConfiguration = VenueUIConfigOptions & {
  displayAddtlInfo: boolean;
  isPitchersView: boolean;
};

export type PitchPalette = {
  header: string;
  ball: string;
  arch: string;
};

export type Configuration = {
  scaling: ScalingConfiguration;
  display: DisplayConfiguration;
  palette: PaletteConfiguration;
};

const ConfigurationContext = createContext<ConfigurationContextType | undefined>(undefined);

const ConfigurationProvider: React.FC<React.PropsWithChildren> = ({ ...rest }) => {
  const { uiConfig, thinConfig } = useConnection();
  const [scaling, setScaling] = useState<ScalingConfiguration>(defaultScaling);
  const [palette, setPalette] = useState<PaletteConfiguration>(defaultPalette);
  const [isAutoZoom, setIsAutoZoom] = useState<boolean>(false);

  const windowSize: WindowSize = useWindowSize();
  const {
    params: { zoom, pitchersView },
  } = useSearchParams();

  useEffect(() => {
    if (isAutoZoom) {
      const browserWidth = windowSize.width;
      const browserHeight = windowSize.height;
      const multiplier = Math.min(browserHeight / defaultScaling.height, browserWidth / defaultScaling.width);
      const height = defaultScaling.height * multiplier;
      const width = defaultScaling.width * multiplier;
      setScaling({ height, width, multiplier });
    }
  }, [isAutoZoom, windowSize]);

  useEffect(() => {
    if ("auto" === zoom) {
      setIsAutoZoom(true);
    } else {
      setIsAutoZoom(false);
      const zoomNum = zoom as number;
      const height = !!zoom ? defaultScaling.height * zoomNum : defaultScaling.height;
      const width = !!zoom ? defaultScaling.width * zoomNum : defaultScaling.width;
      const multiplier = !!zoom ? zoomNum : defaultScaling.multiplier;
      setScaling({ height, width, multiplier });
    }
  }, [zoom]);

  useEffect(() => {
    switch (uiConfig.theme) {
      case "light":
      case "white":
      case "dark":
        setPalette(getPalette(uiConfig.theme));
        break;
      default:
        setPalette(getPalette("light"));
        break;
    }
  }, [uiConfig]);

  return (
    <ConfigurationContext.Provider
      value={{
        config: { scaling, display: createDisplayConfig(uiConfig, pitchersView), palette },
        thinConfig: { scaling, display: createDisplayConfig(thinConfig, pitchersView), palette },
      }}
      {...rest}
    />
  );
};

const useConfiguration = (): ConfigurationContextType => {
  const displayTypeContext = useContext<ConfigurationContextType>(ConfigurationContext);
  if (displayTypeContext === undefined) {
    throw new Error(`useConfiguration must be used within a ConfigurationProvider`);
  }
  return displayTypeContext;
};

export { ConfigurationProvider, useConfiguration };
